"use client";

import { useState } from "react";
import { KeyRound, Copy, Shield } from "lucide-react";
import GeneratorPage from "../GeneratorPage";
import { CATEGORIES } from "@/constants/categories";
import { Button, Input } from "@/components/design-system";
import { generatePasswords, GeneratedPassword, DEFAULT_PASSWORD_OPTIONS } from "@/lib/passwordGenerator";
import { useTranslations, useLocale } from "next-intl";
import { Controller } from "react-hook-form";
import SchemaScript from "@/components/common/SchemaScript";
import { Routes } from "@/constants/routes";
import { Locale } from "@/constants/locales";

export default function PasswordGenerator() {
    const t = useTranslations("password-generator");
    const locale = useLocale() as Locale;
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<(GeneratedPassword & { id: string })[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const [fallbackForm] = useState({
        length: 16,
        count: 5,
        includeUppercase: true,
        includeLowercase: true,
        includeNumbers: true,
        includeSymbols: true,
        excludeAmbiguous: false,
    });

    const handleGenerate = (e: React.FormEvent, formData?: any) => {
        e.preventDefault();
        const data = formData || fallbackForm;

        setIsGenerating(true);

        setTimeout(() => {
            const results = generatePasswords({
                length: Number(data.length),
                includeUppercase: data.includeUppercase !== false,
                includeLowercase: data.includeLowercase !== false,
                includeNumbers: data.includeNumbers !== false,
                includeSymbols: data.includeSymbols !== false,
                excludeAmbiguous: data.excludeAmbiguous === true,
            }, Number(data.count));

            const resultsWithId = results.map(pw => ({
                id: Math.random().toString(36).substr(2, 9),
                ...pw
            }));

            setGenerated(resultsWithId);
            setIsGenerating(false);
        }, 400);
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const handleCopyAll = () => {
        const allText = generated.map(g => g.password).join("\n");
        navigator.clipboard.writeText(allText);
        setCopiedId("all");
        setTimeout(() => setCopiedId(null), 1500);
    };

    const getStrengthColor = (strength: GeneratedPassword['strength']) => {
        switch (strength) {
            case 'weak': return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
            case 'medium': return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'strong': return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
            case 'very-strong': return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
        }
    };

    const getStrengthLabel = (strength: GeneratedPassword['strength']) => {
        return t(`strength.${strength}`);
    };

    const customResultsRenderer = (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                {generated.length > 0 && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleCopyAll}
                    >
                        {copiedId === "all" ? t("results.copyAllSuccess") : t("results.copyAll")}
                    </Button>
                )}
            </div>
            <div className="grid gap-4 md:grid-cols-1">
                {generated.length === 0 ? (
                    <div className="bg-muted/30 border border-dashed border-muted-foreground/25 rounded-lg p-8 text-center min-h-[120px] flex flex-col items-center justify-center">
                        <KeyRound className="h-8 w-8 text-muted-foreground/50 mb-3" />
                        <span className="text-muted-foreground">{t("results.noResults")}</span>
                    </div>
                ) : (
                    generated.map((pw) => (
                        <div key={pw.id} className="relative bg-white dark:bg-slate-900 rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-all group">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleCopy(pw.password, pw.id)}
                                    className="p-2 text-muted-foreground hover:text-primary bg-background border rounded-md shadow-sm"
                                    title={t("form.copyButton")}
                                >
                                    {copiedId === pw.id ? <span className="text-green-500 font-bold text-xs">{t("form.copiedButton")}</span> : <Copy className="h-4 w-4" />}
                                </button>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400">
                                    <Shield className="h-5 w-5" />
                                </div>
                                <div className="space-y-2 flex-1">
                                    <div className="flex gap-2 items-center">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getStrengthColor(pw.strength)}`}>
                                            {getStrengthLabel(pw.strength)}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {pw.password.length} {t("results.characters")}
                                        </span>
                                    </div>
                                    <div className="font-mono text-lg text-slate-900 dark:text-white break-all select-all">{pw.password}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const formConfig = {
        values: fallbackForm,
        fields: [
            { key: "length", label: t("form.length"), type: "number" as const, validation: { required: true, min: 4, max: 128 } },
            { key: "count", label: t("form.count"), type: "number" as const, validation: { required: true, min: 1, max: 50 } },
        ],
        onChange: () => { },
    };

    return (
        <>
            <GeneratorPage
                title={t("title")}
                description={t("description")}
                icon={<KeyRound className="h-4 w-4" />}
                category={CATEGORIES.personal}
                generatedData={generated.length ? generated.map(g => g.password).join("\n") : null}
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                formConfig={formConfig}
                resultsRenderer={customResultsRenderer}
                faqs={t.raw("faqs")}
                similarGenerators={[]}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block mb-1 font-medium">{t("form.length")}</label>
                            <Controller
                                name="length"
                                rules={{ required: true, min: 4, max: 128 }}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        min={4}
                                        max={128}
                                        value={field.value}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <label className="block mb-1 font-medium">{t("form.count")}</label>
                            <Controller
                                name="count"
                                rules={{ required: true, min: 1, max: 50 }}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        min={1}
                                        max={50}
                                        value={field.value}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="flex items-center gap-2">
                            <Controller
                                name="includeUppercase"
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                )}
                            />
                            <label className="text-sm">{t("form.includeUppercase")}</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Controller
                                name="includeLowercase"
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                )}
                            />
                            <label className="text-sm">{t("form.includeLowercase")}</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Controller
                                name="includeNumbers"
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                )}
                            />
                            <label className="text-sm">{t("form.includeNumbers")}</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Controller
                                name="includeSymbols"
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                )}
                            />
                            <label className="text-sm">{t("form.includeSymbols")}</label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Controller
                                name="excludeAmbiguous"
                                render={({ field }) => (
                                    <input
                                        type="checkbox"
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                )}
                            />
                            <label className="text-sm">{t("form.excludeAmbiguous")}</label>
                        </div>
                    </div>
                </div>
            </GeneratorPage>

            <SchemaScript route={Routes['password-generator']} locale={locale} faqItems={t.raw("faqs")} />
        </>
    );
}
